import React, { useState, useMemo, useCallback } from 'react';
import {
    Button,
    IconButton,
    Box,
    Typography,
    TextField,
    Autocomplete,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Paper,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DropzoneArea from '../DropzoneArea';
import AddAccountCardModal from '../../../Widgets/AddAccountCardModal/AddAccountCardModal';
import EnumsService from '../../../../Services/enums.service';
import { toast } from 'react-toastify';

const IcountMoveinDatImportModal = ({
    open,
    onClose,
    caseId,
    accountantId,
    token,
    onSuccess,
    previewIcountMoveinDat,
    importIcountMoveinDat,
    sortCodeIdOptions = [],
    accountCardOptions = [],
    client,
    palCodes = [],
    fetchEnums,
    setLoading,
    setAccountCardOptions,
    t,
    direction = 'rtl',
}) => {
    const [file, setFile] = useState(null);
    const [defaultSortCodeId, setDefaultSortCodeId] = useState('100');
    const [processing, setProcessing] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [showAddAccountCardModal, setShowAddAccountCardModal] = useState(false);
    const [addCardEditedItem, setAddCardEditedItem] = useState(null);

    const cardsWithoutAddItem = useMemo(
        () => (accountCardOptions || []).filter((c) => c.value1 !== 'AddItem'),
        [accountCardOptions]
    );
    const getAccountName = useCallback(
        (code) => {
            if (code == null || code === '') return '';
            const c = cardsWithoutAddItem.find(
                (card) =>
                    String(card.externalSoftwareCardNumber || card.value1 || card.cardId || '') === String(code)
            );
            return c?.name ?? code;
        },
        [cardsWithoutAddItem]
    );
    const sortCodeLabel = useMemo(() => {
        const opt = (sortCodeIdOptions || []).find(
            (o) => String(o.sortCodeId) === String(defaultSortCodeId)
        );
        return opt ? `${opt.sortCodeId} - ${opt.sortCodeName ?? ''}` : defaultSortCodeId;
    }, [sortCodeIdOptions, defaultSortCodeId]);

    const palCodesFormatted = useMemo(
        () =>
            (palCodes || []).map((item) => ({
                value: item.palCodeId ?? item.value,
                label: item.palCodeId ?? item.label ?? item.value ?? '',
            })),
        [palCodes]
    );

    const handleChangeAfterAdd = useCallback(async () => {
        if (client?.caseId && token && setAccountCardOptions) {
            try {
                const res = await EnumsService.GetAccountCardsByCaseId(client.caseId, token);
                if (res?.ok) {
                    const data = await res.json();
                    setAccountCardOptions([{ name: t('AddAccountCard'), value1: 'AddItem' }, ...(data || [])]);
                }
            } catch (e) {
                console.error('Refresh account cards after add:', e);
            }
        }
        if (fetchEnums) await fetchEnums();
        setShowAddAccountCardModal(false);
        if (file && caseId && accountantId && previewIcountMoveinDat) {
            try {
                setProcessing(true);
                const res = await previewIcountMoveinDat(caseId, accountantId, file, defaultSortCodeId || '100', token);
                setPreviewData({
                    entries: res.entries ?? [],
                    missingAccountCards: res.missingAccountCards ?? [],
                    errorMessages: res.errorMessages ?? [],
                    totalLines: res.totalLines ?? 0,
                    validCount: res.validCount ?? 0,
                    errorCount: res.errorCount ?? 0,
                });
                setPage(0);
            } catch (err) {
                console.error('iCount Movein.DAT preview refresh failed:', err);
            } finally {
                setProcessing(false);
            }
        }
    }, [client?.caseId, token, setAccountCardOptions, t, fetchEnums, file, caseId, accountantId, previewIcountMoveinDat, defaultSortCodeId]);

    const handleDrop = (files) => {
        if (files?.length) setFile(files[0]);
        setPreviewData(null);
    };

    const handlePreview = async () => {
        if (!file || !caseId || !accountantId || !token || !previewIcountMoveinDat) return;
        try {
            setProcessing(true);
            const res = await previewIcountMoveinDat(caseId, accountantId, file, defaultSortCodeId || '100', token);
            setPreviewData({
                entries: res.entries ?? [],
                missingAccountCards: res.missingAccountCards ?? [],
                errorMessages: res.errorMessages ?? [],
                totalLines: res.totalLines ?? 0,
                validCount: res.validCount ?? 0,
                errorCount: res.errorCount ?? 0,
            });
            setPage(0);
        } catch (err) {
            console.error('iCount Movein.DAT preview failed:', err);
            setPreviewData({
                entries: [],
                missingAccountCards: [],
                errorMessages: [err.message || 'Preview failed'],
                totalLines: 0,
                validCount: 0,
                errorCount: 1,
            });
        } finally {
            setProcessing(false);
        }
    };

    const handleCommit = async () => {
        if (!file || !caseId || !token || !importIcountMoveinDat) return;
        const missing = previewData?.missingAccountCards ?? [];
        if (missing.length > 0) return;
        try {
            setProcessing(true);
            const res = await importIcountMoveinDat(caseId, file, defaultSortCodeId || '100', token);
            const processed = res?.processedCount ?? 0;
            const errors = res?.errorCount ?? 0;
            const messages = res?.errorMessages ?? [];
            if (errors > 0 && messages.length > 0) {
                toast.error(`${t('ImportFailed')} – ${t('processed')}: ${processed}, ${t('Errors')}: ${errors}. ${messages.slice(0, 3).join(' ')}`);
            } else if (errors > 0) {
                toast.warning(`${t('ImportSuccessful')} – ${processed} ${t('processed')}. ${t('Errors')}: ${errors}`);
            } else {
                toast.success(processed > 0 ? `${t('ImportSuccessful')} – ${processed} ${t('processed')}` : t('ImportSuccessful'));
            }
            onSuccess?.();
            handleClose();
        } catch (err) {
            console.error('iCount Movein.DAT import failed:', err);
            toast.error(t('ImportFailed') || err?.message || 'Import failed');
        } finally {
            setProcessing(false);
        }
    };

    const handleClose = () => {
        setFile(null);
        setPreviewData(null);
        setPage(0);
        setRowsPerPage(10);
        onClose();
    };

    const formatAmount = (amount) => {
        if (amount == null) return '';
        return new Intl.NumberFormat('he-IL', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(amount);
    };

    const entries = previewData?.entries ?? [];
    const previewSummaries = useMemo(() => {
        const valid = entries.filter((e) => e.isValid);
        const sumAmount = valid.reduce((s, e) => s + (Number(e.amount) || 0), 0);
        const hasPrm = valid.some((e) => e.gross != null || e.net != null || e.vat != null);
        const sumGross = hasPrm ? valid.reduce((s, e) => s + (Number(e.gross) ?? Number(e.amount) ?? 0), 0) : null;
        const sumNet = hasPrm ? valid.reduce((s, e) => s + (Number(e.net) ?? 0), 0) : null;
        const sumVat = hasPrm ? valid.reduce((s, e) => s + (Number(e.vat) ?? 0), 0) : null;
        return { sumAmount, sumGross, sumNet, sumVat, hasPrm };
    }, [entries]);

    if (!open) return null;

    const missingAccountCards = previewData?.missingAccountCards ?? [];
    const hasMissingCards = missingAccountCards.length > 0;
    const paginatedEntries = entries.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    return (
        <>
        <div className="download-modal-overlay" onClick={() => { if (!showAddAccountCardModal) handleClose(); }}>
            <div className="download-modal" onClick={(e) => e.stopPropagation()}>
                <div
                    className={`download-modal-content ${direction}`}
                    style={{
                        position: 'relative',
                        maxWidth: previewData ? '1100px' : '560px',
                    }}
                >
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <h4 className="report-title">
                            iCount Movein.DAT – {t('Preview')} & {t('Import')}
                        </h4>
                        <IconButton className="client-back-icon" onClick={handleClose} sx={{ padding: '4px' }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Box display="flex" flexDirection="column" gap={2}>
                        <div>
                            <h5>.dat</h5>
                            <DropzoneArea
                                key={file ? file.name : 'icount-empty'}
                                onDrop={handleDrop}
                                accept=".dat"
                            />
                            {file && (
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                    {t('FileSelected')}: {file.name}
                                </Typography>
                            )}
                        </div>

                        {sortCodeIdOptions?.length > 0 ? (
                            <Autocomplete
                                options={sortCodeIdOptions}
                                getOptionLabel={(option) =>
                                    `${option.sortCodeId} - ${option.sortCodeName ?? ''}`
                                }
                                value={
                                    sortCodeIdOptions.find(
                                        (option) => String(option.sortCodeId) === String(defaultSortCodeId)
                                    ) || null
                                }
                                onChange={(event, newValue) => {
                                    const newSortCodeId = newValue ? String(newValue.sortCodeId) : '100';
                                    setDefaultSortCodeId(newSortCodeId);
                                    setPreviewData(null);
                                }}
                                size="small"
                                sx={{ maxWidth: 280 }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={t('DefaultSortCode') || 'Default sort code'}
                                        variant="standard"
                                    />
                                )}
                            />
                        ) : (
                            <TextField
                                label={t('DefaultSortCode') || 'Default sort code'}
                                value={defaultSortCodeId}
                                onChange={(e) => {
                                    setDefaultSortCodeId(e.target.value);
                                    setPreviewData(null);
                                }}
                                size="small"
                                sx={{ maxWidth: 280 }}
                                variant="standard"
                            />
                        )}

                        <Box display="flex" gap={1.5} mt={1}>
                            <Button
                                variant="contained"
                                onClick={handlePreview}
                                disabled={!file || processing}
                            >
                                {processing && !previewData ? t('Processing') : t('Preview')}
                            </Button>
                            <Button
                                variant="contained"
                                color="success"
                                onClick={handleCommit}
                                disabled={
                                    !previewData ||
                                    processing ||
                                    hasMissingCards ||
                                    (previewData?.validCount ?? 0) === 0
                                }
                            >
                                {processing && previewData ? t('Processing') : t('Import')}
                            </Button>
                        </Box>
                    </Box>

                    {previewData && (
                        <Box mt={3} sx={{ maxHeight: 600, overflow: 'auto' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {t('TotalLines') || 'Total lines'}: <strong>{previewData.totalLines}</strong>
                                {' | '}
                                {t('ValidCount') || 'Valid'}: <strong>{previewData.validCount}</strong>
                                {' | '}
                                {t('ErrorCount') || 'Errors'}: <strong>{previewData.errorCount}</strong>
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {t('SumAmount') || 'Sum Amount'}: <strong>{formatAmount(previewSummaries.sumAmount)}</strong>
                                {previewSummaries.hasPrm && previewSummaries.sumGross != null && (
                                    <> | {t('MoveinGross') || 'Gross'}: <strong>{formatAmount(previewSummaries.sumGross)}</strong></>
                                )}
                                {previewSummaries.hasPrm && previewSummaries.sumNet != null && (
                                    <> | {t('MoveinNet') || 'Net'}: <strong>{formatAmount(previewSummaries.sumNet)}</strong></>
                                )}
                                {previewSummaries.hasPrm && previewSummaries.sumVat != null && (
                                    <> | {t('MoveinVat') || 'VAT'}: <strong>{formatAmount(previewSummaries.sumVat)}</strong></>
                                )}
                            </Typography>

                            {hasMissingCards && (
                                <Box
                                    mb={2}
                                    p={1.5}
                                    bgcolor="warning.light"
                                    borderRadius={1}
                                    sx={{ maxHeight: 220, overflow: 'auto' }}
                                >
                                    <Typography variant="subtitle2" color="warning.dark" sx={{ mb: 0.5 }}>
                                        {t('MissingAccountCards') || 'Missing account cards'} ({missingAccountCards.length})
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        {t('AddMissingCardsBeforeImport')}
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                                        {missingAccountCards.map((code, i) => (
                                            <Button
                                                key={i}
                                                variant="outlined"
                                                size="small"
                                                startIcon={<AddIcon />}
                                                onClick={() => {
                                                    setAddCardEditedItem({ accountCodeId: code });
                                                    setShowAddAccountCardModal(true);
                                                }}
                                                sx={{ textTransform: 'none' }}
                                            >
                                                {code}
                                            </Button>
                                        ))}
                                    </Box>
                                </Box>
                            )}

                            {previewData.errorMessages?.length > 0 && (
                                <Box
                                    mb={2}
                                    p={1.5}
                                    bgcolor="error.light"
                                    borderRadius={1}
                                    sx={{ maxHeight: 220, overflow: 'auto' }}
                                >
                                    <Typography variant="subtitle2" color="error.dark" sx={{ mb: 0.5 }}>
                                        {t('Errors') || 'Errors'} ({previewData.errorMessages.length})
                                    </Typography>
                                    <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                        {previewData.errorMessages.map((msg, i) => (
                                            <li key={i}>
                                                <Typography variant="body2" component="span" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                    {msg}
                                                </Typography>
                                            </li>
                                        ))}
                                    </Box>
                                </Box>
                            )}

                            {entries.length > 0 && (
                                <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                                    <Table size="small" stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('Line') || 'Line'}</TableCell>
                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('DocumentDate') || 'Date'}</TableCell>
                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('Reference')}</TableCell>
                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('DebitAccount') || 'Debit'}</TableCell>
                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('DebitName')}</TableCell>
                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('CreditAccount') || 'Credit'}</TableCell>
                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('CreditName')}</TableCell>
                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('SortCode')}</TableCell>
                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('Description')}</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 700, fontSize: 12 }}>{t('Amount')}</TableCell>
                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>VAT%</TableCell>
                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('Status') || 'Status'}</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {paginatedEntries.map((row, i) => (
                                                <TableRow
                                                    key={row.lineNumber ?? i}
                                                    sx={{ bgcolor: row.isValid ? 'transparent' : 'action.hover' }}
                                                >
                                                    <TableCell sx={{ fontSize: 12 }}>{row.lineNumber}</TableCell>
                                                    <TableCell sx={{ fontSize: 12 }}>{row.documentDate}</TableCell>
                                                    <TableCell sx={{ fontSize: 12 }}>{row.reference}</TableCell>
                                                    <TableCell sx={{ fontSize: 12 }}>{row.debitAccount}</TableCell>
                                                    <TableCell sx={{ fontSize: 12, maxWidth: 160, whiteSpace: 'normal', wordBreak: 'break-word' }} title={row.debitAccountName ?? getAccountName(row.debitAccount)}>
                                                        {(() => { const n = row.debitAccountName ?? getAccountName(row.debitAccount); return (n && n !== String(row.debitAccount)) ? n : '—'; })()}
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: 12 }}>{row.creditAccount}</TableCell>
                                                    <TableCell sx={{ fontSize: 12, maxWidth: 160, whiteSpace: 'normal', wordBreak: 'break-word' }} title={row.creditAccountName ?? getAccountName(row.creditAccount)}>
                                                        {(() => { const n = row.creditAccountName ?? getAccountName(row.creditAccount); return (n && n !== String(row.creditAccount)) ? n : '—'; })()}
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: 12 }} title={sortCodeLabel}>{sortCodeLabel}</TableCell>
                                                    <TableCell sx={{ fontSize: 12, maxWidth: 200, whiteSpace: 'normal', wordBreak: 'break-word' }} title={row.description}>
                                                        {row.description ?? ''}
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ fontSize: 12 }}>{formatAmount(row.amount)}</TableCell>
                                                    <TableCell sx={{ fontSize: 12 }}>{row.vatPercent != null ? row.vatPercent : '—'}</TableCell>
                                                    <TableCell sx={{ fontSize: 12, maxWidth: 320, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                        {row.isValid ? (
                                                            <Typography variant="body2" color="success.main">{t('Valid') || 'OK'}</Typography>
                                                        ) : (
                                                            <Typography variant="body2" color="error.main" title={row.errorMessage}>
                                                                {row.errorMessage ?? ''}
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                    <TablePagination
                                        component="div"
                                        count={entries.length}
                                        page={page}
                                        onPageChange={(_, p) => setPage(p)}
                                        rowsPerPage={rowsPerPage}
                                        onRowsPerPageChange={(e) => {
                                            setRowsPerPage(parseInt(e.target.value, 10));
                                            setPage(0);
                                        }}
                                        rowsPerPageOptions={[10, 25, 50]}
                                        labelRowsPerPage={t('RowsPerPage') || 'Rows per page'}
                                    />
                                </TableContainer>
                            )}
                        </Box>
                    )}
                </div>
            </div>
        </div>

        {client && token && (
            <AddAccountCardModal
                accountCardOptions={accountCardOptions}
                open={showAddAccountCardModal}
                onClose={() => { setShowAddAccountCardModal(false); setAddCardEditedItem(null); }}
                editedItem={addCardEditedItem || {}}
                isRange={false}
                isClosedReportingMonth={false}
                palCodes={palCodesFormatted}
                propPalCode={null}
                direction={direction}
                client={client}
                token={token}
                setLoading={setLoading}
                handleChangeAfterAdd={handleChangeAfterAdd}
                fetchEnums={fetchEnums}
                type="main"
                isEdit={false}
                accountCards={accountCardOptions}
            />
        )}
        </>
    );
};

export default IcountMoveinDatImportModal;
